// client/src/components/SearchBar.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_ENDPOINTS } from "../config/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  faTag,
  faMagnifyingGlass,
  faPlus,
  faFingerprint,
} from "@fortawesome/free-solid-svg-icons";

const SUGGESTED_TAGS = [
  "Vanity",
  "Vintage",
  "Exotic",
  "Modified",
  "Rare",
  "Funny",
];

const SearchBar = ({ compact = false }) => {
  const [plate, setPlate] = useState("");
  const [vehicleCount, setVehicleCount] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [placeholderText, setPlaceholderText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [singleDigitMode, setSingleDigitMode] = useState(false);
  const [shiningTagIndex, setShiningTagIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const examples = [
    "P9999",
    "wtf",
    "rapid 111",
    "88",
    "utm",
    "4321",
    "Putrajaya",
    "asean",
  ];

  // Initialize search and mode from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get("search");
    const singleDigitModeFromUrl =
      searchParams.get("singleDigitMode") === "true";

    // Always sync plate with URL search param (including clearing it)
    setPlate(searchFromUrl || "");

    // Reset single digit mode if no search or if search isn't a single digit
    if (!searchFromUrl || !/^[0-9]$/.test(searchFromUrl)) {
      setSingleDigitMode(false);
    } else if (/^[0-9]$/.test(searchFromUrl) && singleDigitModeFromUrl) {
      setSingleDigitMode(true);
    }
  }, [location.search]);

  useEffect(() => {
    // Start the typing animation after 5 seconds
    const initialDelay = setTimeout(() => {
      setIsTyping(true);
    }, 5000);

    return () => clearTimeout(initialDelay);
  }, []);

  useEffect(() => {
    if (!isTyping) return;

    const animateText = async (index = 0) => {
      const example = examples[index];

      // Type the text
      for (let i = 0; i <= example.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        setPlaceholderText(example.slice(0, i));
      }

      // Pause at full text
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Backspace the text
      for (let i = example.length; i >= 0; i--) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        setPlaceholderText(example.slice(0, i));
      }

      // Pause before next word
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Move to next example or restart
      animateText((index + 1) % examples.length);
    };

    animateText();
  }, [isTyping]);

  useEffect(() => {
    if (!compact) {
      const fetchVehicleCount = async () => {
        try {
          const response = await fetch(API_ENDPOINTS.VEHICLE_COUNT);
          const data = await response.json();
          setVehicleCount(data.count);
        } catch (err) {
          console.error("Error fetching vehicle count:", err);
        }
      };
      fetchVehicleCount();
    }
  }, [compact]);

  // Check if the search is a single digit
  const isSingleDigit = /^[0-9]$/.test(plate.trim());

  // Add effect for tag animation
  useEffect(() => {
    const interval = setInterval(() => {
      setShiningTagIndex(
        (prevIndex) => (prevIndex + 1) % SUGGESTED_TAGS.length
      );
    }, 2000); // Change shining tag every 2 seconds

    return () => clearInterval(interval);
  }, []);

  const handleCountHover = () => {
    // Random position offset (-20px to 20px)
    const xOffset = Math.random() * 40 - 20;
    const yOffset = Math.random() * 40 - 20;
    // Random rotation (-10deg to 10deg)
    const rotation = Math.random() * 20 - 10;

    // Get random doge phrase from translations
    const phraseKeys = Array.from(
      { length: 11 },
      (_, i) => `search.doge_phrases.${i}`
    );
    const randomKey = phraseKeys[Math.floor(Math.random() * phraseKeys.length)];
    const phrase = t(randomKey);

    setTooltipStyle({
      transform: `translate(${xOffset}px, ${yOffset}px) rotate(${rotation}deg)`,
    });
    setTooltipText(phrase);
    setShowTooltip(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSearching(true);
    setTimeout(() => {
      // Start with existing search params to preserve other parameters like page, limit, sort
      const searchParams = new URLSearchParams(location.search);

      if (plate.trim()) {
        searchParams.set("search", plate.trim());
        // Only add singleDigitMode if it's enabled AND we have a single digit
        if (singleDigitMode && /^[0-9]$/.test(plate.trim())) {
          searchParams.set("singleDigitMode", "true");
        } else {
          searchParams.delete("singleDigitMode");
        }
      } else {
        // Clear search-related params when empty
        searchParams.delete("search");
        searchParams.delete("singleDigitMode");
      }

      // Clear any existing startsWith or tag params as they shouldn't coexist with search
      searchParams.delete("startsWith");
      searchParams.delete("tag");

      navigate(
        `/explore${
          searchParams.toString() ? `?${searchParams.toString()}` : ""
        }`,
        {
          state: { shouldScroll: true },
        }
      );
      setIsSearching(false);
    }, 700);
  };

  // Reset single digit mode when input changes
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setPlate(newValue);
    // Only disable single digit mode if the input is not a single digit
    if (!/^[0-9]$/.test(newValue.trim())) {
      setSingleDigitMode(false);
    }
  };

  const handleTagClick = (tag) => {
    const searchParams = new URLSearchParams();
    searchParams.set("tag", tag);
    navigate(`/explore?${searchParams.toString()}`, {
      state: { shouldScroll: true },
    });
  };

  const handleAddNew = () => {
    if (plate.trim()) {
      setIsSpinning(true);
      setTimeout(() => {
        navigate(`/plate/${plate.trim()}`);
        setPlate("");
        setIsSpinning(false);
      }, 600); // Match animation duration
    }
  };

  return (
    <div className={`mx-auto text-center ${compact ? "" : "max-w-3xl"}`}>
      {!compact && vehicleCount !== null && (
        <div className="mb-3 space-y-2">
          <h2 className="font-bold text-accent-600 relative group">
            <span
              onMouseEnter={handleCountHover}
              onMouseLeave={() => setShowTooltip(false)}
              className="cursor-help inline-block animate-[scale_2s_ease-in-out_infinite]"
            >
              {vehicleCount.toLocaleString()} {t("search.vehicles_spotted")}
            </span>
            {showTooltip && (
              <span
                className="absolute left-1/2 -translate-x-1/2 -top-8 px-3 py-1
                         bg-yellow-100 text-yellow-800 rounded-lg text-sm
                         font-['Comic_Sans_MS','Comic_Sans',cursive]
                         transition-all duration-200 z-10
                         border-2 border-yellow-300 shadow-lg
                         whitespace-nowrap dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-700"
                style={tooltipStyle}
              >
                {tooltipText}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-600">{t("search.description")}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={`relative group ${compact ? "" : "mb-4"}`}
      >
        <input
          type="text"
          value={plate}
          onChange={handleInputChange}
          placeholder={placeholderText || t("search.placeholder")}
          className="w-full p-3 text-lg border-2 border-primary-500/30 rounded-lg shadow-lg dark:bg-gray-800
                   focus:border-primary-600 focus:ring-2 focus:ring-primary-300 focus:outline-none
                   transition-all duration-300 bg-white
                   placeholder:text-gray-400
                   sm:placeholder:text-base placeholder:text-sm"
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1 items-center">
          {isSingleDigit && (
            <button
              type="button"
              onClick={() => setSingleDigitMode(!singleDigitMode)}
              className={`px-2 py-1.5 transition-all duration-300
                       flex items-center gap-1.5
                       rounded-md text-sm
                       ${
                         singleDigitMode
                           ? "bg-primary-100 text-primary-600 border-2 border-primary-200 hover:bg-primary-200"
                           : "bg-gray-50 text-gray-500 hover:bg-gray-100 border-2 border-gray-200"
                       }`}
              title={
                singleDigitMode
                  ? `${t("search.single_digit_on")}`
                  : `${t("search.single_digit_off")}`
              }
            >
              <FontAwesomeIcon
                icon={faFingerprint}
                className={`w-4 h-4 ${
                  singleDigitMode ? "text-primary-500" : "text-gray-400"
                }`}
              />
              <span className="whitespace-nowrap">
                {singleDigitMode
                  ? t("search.single_digit_on")
                  : t("search.single_digit_off")}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={handleAddNew}
            disabled={!plate.trim()}
            className={`px-3 py-1.5 transition-all duration-300 hover:scale-105
                     ${
                       plate.trim()
                         ? "text-primary-500 hover:text-primary-600"
                         : "text-gray-300 cursor-not-allowed hover:scale-100"
                     }
                     ${
                       isSpinning
                         ? "animate-[spin-fast_0.6s_cubic-bezier(0.1,0.7,0.7,1)]"
                         : ""
                     }`}
            title={t("search.add_new")}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            type="submit"
            className={`px-4 py-1.5 rounded-md transition-all duration-300
                     shadow-md hover:shadow-lg
                     ${
                       plate.trim()
                         ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900"
                         : "bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700"
                     }
                     hover:scale-105
                     ${
                       isSearching
                         ? "animate-[zoom-shake_0.7s_cubic-bezier(0.36,0,0.66,1)]"
                         : "animate-pulse"
                     }`}
            title={plate.trim() ? t("search.button") : t("explore.title")}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
        </div>
      </form>

      {!compact && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <FontAwesomeIcon
              icon={faTag}
              className="w-3 h-3 text-primary-500"
            />
            {t("search.or_browse")}:
          </span>
          {SUGGESTED_TAGS.map((tag, index) => (
            <button
              key={tag}
              onClick={() => handleTagClick(tag)}
              className={`text-sm px-3 py-1 rounded-full transition-all duration-300 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700
                ${
                  shiningTagIndex === index
                    ? "bg-primary-100 text-primary-700 border border-primary-200 shadow-sm scale-105 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-200 dark:hover:border-gray-500"
                    : "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 hover:scale-105 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200 dark:hover:border-gray-500"
                }`}
            >
              {t(`tags.labels.${tag}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
